import { alpha, createTheme, Theme, ThemeOptions } from '@mui/material';
import { deepmerge } from '@mui/utils';
import {
  errorPalette,
  greenPalette,
  infoPalette,
  neutralsPalette,
  orangePalette,
  primaryPalette,
  purplePalette,
  successPalette,
  warningPalette,
} from '@shared/theme/colors';

const shadowDimensions = {
  xs: '0px 2px 2px',
  s: '0px 5px 15px',
  m: '0px 8px 23px',
  l: '0px 5px 45px',
  xl: '0px 5px 65px',
};
const borderRadii = {
  s: 2,
  m: 4,
  l: 10,
  xl: 16,
  xxl: 24,
  infinite: 9999,
  circle: '50%',
};

const createMuiTheme = (options: ThemeOptions, localization?: any) => {
  const theme = createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 720,
          md: 990,
          lg: 1280,
          xl: 1800,
        },
      },
      // TODO: investigate how to enable this and also have white labeling working
      // cssVariables: true,
      spacing: 4,
      ...options,
      palette: {
        ...options.palette,
        primary: {
          ...primaryPalette,
          ...options.palette?.primary,
        },
        info: {
          ...infoPalette,
        },
        warning: {
          ...warningPalette,
          ...options.palette?.warning,
        },
        error: {
          ...errorPalette,
          ...options.palette?.error,
        },
        success: {
          ...successPalette,
          ...options.palette?.success,
        },
      },
    },
    localization,
  ) as CommonTheme;
  theme.palette.hobby = greenPalette;
  theme.palette.job = orangePalette;
  theme.palette.education = purplePalette;
  theme.palette.neutrals = neutralsPalette;

  theme.sizing = (unit: number) => `${0.25 * unit}rem`;
  theme.zIndex.positionSticky = 1000;
  theme.spacing.px = 4;
  theme.cssVars = { defaults: {} };
  theme.borderRadius = (...args) =>
    args
      .map((arg) => {
        const value = arg in borderRadii ? borderRadii[arg as keyof typeof borderRadii] : arg;
        return typeof value === 'number' ? `${value}px` : value;
      })
      .join(' ');
  theme.shadow = (dimensions, color = neutralsPalette[600]) =>
    `${shadowDimensions[dimensions]} ${alpha(color, 0.3)}`;
  theme.linearGradient = (direction, colors) =>
    `linear-gradient(${typeof direction === 'number' ? `${direction}deg` : direction}, ${colors
      .map((colorStop) => {
        const [color, offset] = Array.isArray(colorStop) ? colorStop : [colorStop];
        return `${color} ${typeof offset === 'number' ? `${offset}%` : offset || ''}`;
      })
      .join(', ')})`;
  const defaultTypographyTag = 'p';
  theme.components = deepmerge(theme.components, {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          title: defaultTypographyTag,
          h1: defaultTypographyTag,
          h2: defaultTypographyTag,
          h3: defaultTypographyTag,
          h4: defaultTypographyTag,
          h5: defaultTypographyTag,
          h6: defaultTypographyTag,
          subtitle1: defaultTypographyTag,
          subtitle2: defaultTypographyTag,
          subtitle3: defaultTypographyTag,
          body1: defaultTypographyTag,
          body2: defaultTypographyTag,
          body3: defaultTypographyTag,
        },
      },
    },
  });
  return theme;
};

export type CommonTheme = Omit<Theme, 'shadows'> & {
  palette: {
    primary: typeof primaryPalette;
    info: typeof primaryPalette;
    error: typeof errorPalette;
    success: typeof successPalette;
    warning: typeof orangePalette;
    neutrals: typeof neutralsPalette;
    hobby: typeof greenPalette;
    job: typeof orangePalette;
    education: typeof purplePalette;
  };
  zIndex: { positionSticky: 1000 };
  sizing: (unit: number) => string;
  borderRadius: (...options: Array<keyof typeof borderRadii | number>) => string;
  shadow: (dimensions: keyof typeof shadowDimensions, color?: string) => string;
  /**
   * @deprecated use the shadow('size') function instead
   * */
  shadows: any;
  spacing: { px: number };
  cssVars: { defaults: Record<string, string> };
  linearGradient: (
    direction: string | number,
    colors: Array<string | [string, string | number]>,
  ) => string;
};

export default createMuiTheme;
